import {
  BOOKING_TYPE_TO_SERVICE_ID,
  DEFAULT_SERVICE_PRICES,
  loadPublishedAvailability,
} from '../../referrer/utils/servicesAvailabilityStorage';
import type { Professional } from '../data/bookingData';
import { SERVICE_CONFIGS } from '../data/bookingData';
import type { ServiceType } from '../data/bookingData';
import { fallbackRankScore } from '../../../shared/utils/ranking';

type CachedApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

export type MarketplaceApiClient = {
  getReferrers: () => Promise<CachedApiResponse<unknown[]>>;
  getServiceConfigurationsForService: (serviceId: string) => Promise<CachedApiResponse<unknown[]>>;
};

const CACHE_TTL_MS = 60_000;

interface MarketplaceBaseData {
  referrers: any[];
  configsByService: Record<string, any[]>;
  unifiedConfigs: any[];
}

let cache: { data: MarketplaceBaseData; fetchedAt: number } | null = null;
let inflight: Promise<MarketplaceBaseData> | null = null;

function normalizeProfileSkills(skills: unknown): string[] {
  if (Array.isArray(skills)) {
    return skills.map(String).filter(Boolean);
  }
  if (typeof skills === 'string' && skills.trim()) {
    return skills.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export function buildProfessionalsForService(
  serviceType: ServiceType,
  profiles: any[],
  serviceConfigs: any[],
  unifiedConfigs: any[]
): Professional[] {
  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  const svcConfig = SERVICE_CONFIGS[serviceType];
  const serviceConfigByUser = new Map(serviceConfigs.map((row: any) => [row.user_id, row]));

  const mapped: Professional[] = profiles.map((profile: any) => {
    const customConfig = serviceConfigByUser.get(profile.id);
    const unifiedConfig = unifiedConfigs.find((c: any) => c.user_id === profile.id);

    const fallbackPrice = (serviceId && DEFAULT_SERVICE_PRICES[serviceId]) || 500;
    let price = fallbackPrice;
    if (customConfig && customConfig.price !== null) {
      price = customConfig.price;
    }

    let effectiveDuration: number | null = null;
    if (svcConfig.schedulingModel === 'fixed_duration_call') {
      effectiveDuration = svcConfig.slotDurationMinutes;
    } else if (svcConfig.schedulingModel === 'variable_duration_call') {
      effectiveDuration =
        customConfig?.slot_duration_minutes ??
        customConfig?.referral_call_duration ??
        (svcConfig.allowedDurations ? svcConfig.allowedDurations[0] : 20);
    }

    let availabilityMap = unifiedConfig?.availability || customConfig?.availability || {};
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) {
      const localPub = loadPublishedAvailability(profile.id);
      availabilityMap = localPub['unified-availability'] || (serviceId ? localPub[serviceId] : undefined) || {};
    }
    const profileSkills = normalizeProfileSkills(profile.skills);
    const rating = typeof profile.rating === 'number' ? profile.rating : 0.0;
    const reviews = typeof profile.reviews_count === 'number' ? profile.reviews_count : 0;
    // Server-computed multi-signal rank when available (migration 026);
    // otherwise a client-side Bayesian-rating fallback.
    const rankScore =
      typeof profile.rank_score === 'number'
        ? profile.rank_score
        : fallbackRankScore(profile.rating, reviews);

    return {
      id: profile.id,
      alias: profile.full_name || 'Referrer',
      name: profile.full_name || 'Referrer',
      title: profile.designation || 'Referrer',
      company: profile.company_name || undefined,
      rating,
      reviews,
      rankScore,
      price,
      description: profile.bio || 'Professional available for mentorship sessions.',
      specialties: profileSkills,
      skills: profileSkills,
      weeklySlots: [],
      image: profile.profile_picture || undefined,
      yearsOfExperience: profile.years_of_experience || undefined,
      verified: !!profile.is_verified,
      designation: profile.designation || undefined,
      bio: profile.bio || undefined,
      location: profile.location || undefined,
      industry: profile.industry || undefined,
      slotDurationMinutes: effectiveDuration ?? undefined,
      availabilityMap,
      offersService: Boolean(customConfig),
    } as Professional;
  });

  return mapped.sort((a, b) => {
    const aOffers = (a as any).offersService ? 1 : 0;
    const bOffers = (b as any).offersService ? 1 : 0;
    if (bOffers !== aOffers) return bOffers - aOffers;
    return (
      (b.rankScore ?? 0) - (a.rankScore ?? 0) ||
      b.rating - a.rating ||
      b.reviews - a.reviews
    );
  });
}

export function getCachedProfessionalsForService(serviceType: ServiceType): Professional[] | null {
  if (!cache || Date.now() - cache.fetchedAt > CACHE_TTL_MS) {
    return null;
  }

  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  if (!serviceId) return null;

  return buildProfessionalsForService(
    serviceType,
    cache.data.referrers,
    cache.data.configsByService[serviceId] || [],
    cache.data.unifiedConfigs
  );
}

export async function fetchMarketplaceBaseData(client: MarketplaceApiClient): Promise<MarketplaceBaseData> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.data;
  }

  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    const [
      referrersRes,
      resumeReviewRes,
      interviewMockRes,
      referralSessionRes,
      unifiedRes,
    ] = await Promise.all([
      client.getReferrers(),
      client.getServiceConfigurationsForService('resume-review'),
      client.getServiceConfigurationsForService('interview-mock'),
      client.getServiceConfigurationsForService('referral-session'),
      client.getServiceConfigurationsForService('unified-availability'),
    ]);

    const referrersOk = referrersRes.success && Array.isArray(referrersRes.data);
    if (!referrersOk) {
      if (cache) {
        inflight = null;
        return cache.data;
      }
      throw new Error(referrersRes.error || 'Failed to load referrers');
    }

    const data: MarketplaceBaseData = {
      referrers: referrersRes.data ?? [],
      configsByService: {
        'resume-review':
          resumeReviewRes.success && resumeReviewRes.data ? resumeReviewRes.data : cache?.data.configsByService['resume-review'] || [],
        'interview-mock':
          interviewMockRes.success && interviewMockRes.data ? interviewMockRes.data : cache?.data.configsByService['interview-mock'] || [],
        'referral-session':
          referralSessionRes.success && referralSessionRes.data ? referralSessionRes.data : cache?.data.configsByService['referral-session'] || [],
      },
      unifiedConfigs:
        unifiedRes.success && unifiedRes.data ? unifiedRes.data : cache?.data.unifiedConfigs || [],
    };

    cache = { data, fetchedAt: Date.now() };
    inflight = null;
    return data;
  })().catch((error) => {
    inflight = null;
    throw error;
  });

  return inflight;
}

export function mapCachedProfessionals(serviceType: ServiceType, base: MarketplaceBaseData): Professional[] {
  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  return buildProfessionalsForService(
    serviceType,
    base.referrers,
    serviceId ? base.configsByService[serviceId] || [] : [],
    base.unifiedConfigs
  );
}

export function invalidateMarketplaceCache() {
  cache = null;
  inflight = null;
}
